import { defineStore } from 'pinia';
import { ref } from 'vue';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  action?: { label: string; run: () => void };
}

let nextId = 1;

export const useToastStore = defineStore('toasts', () => {
  const toasts = ref<Toast[]>([]);
  const timers = new Map<number, ReturnType<typeof setTimeout>>();

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id);
    const timer = timers.get(id);
    if (timer) clearTimeout(timer);
    timers.delete(id);
  }

  function show(message: string, tone: ToastTone = 'info', options: { action?: Toast['action']; durationMs?: number } = {}): number {
    const duplicate = toasts.value.find((toast) => toast.message === message);
    if (duplicate) dismiss(duplicate.id);
    const id = nextId++;
    toasts.value = [...toasts.value.slice(-3), { id, message, tone, action: options.action }];
    const duration = options.durationMs ?? (tone === 'danger' || options.action ? 5000 : 3000);
    timers.set(id, setTimeout(() => dismiss(id), duration));
    return id;
  }

  return {
    toasts,
    show,
    dismiss,
    success: (message: string) => show(message, 'success'),
    error: (message: string, action?: Toast['action']) => show(message, 'danger', { action }),
    info: (message: string) => show(message, 'info'),
    warning: (message: string) => show(message, 'warning'),
  };
});
